import React, { useState, useMemo } from 'react';
import { Project } from '../../models';
import { useAppStyles } from '../../styles';
import { IntlProps, TrackContext } from '../common';
import LoadingCard from '../common/loading-card';
import DeleteProjectDialog from './delete-project-dialog';
import EmptyState from './empty-state';
import ProjectCard from './project-card';
import UpdateProjectDialog from './update-project-dialog';
import { Dropdown, Option, Label } from '@fluentui/react-components';
import { compareDates, compareNoCase } from '@microsoft/vscodeedu-api';
import { injectIntl } from 'react-intl';

type SortOption = 'title-asc' | 'title-desc' | 'lastUpdated-asc' | 'lastUpdated-desc';

// Project card list properties.
export type ProjectListProps = Readonly<
    IntlProps<{
        projects: ReadonlyArray<Project>;
        isLoading: boolean;
        onCreate: () => void;
        onPublish: (projectId: string) => void;
        onShowPublished: () => void;
        onUpdate: (project: Project) => void;
        onDelete: (projectId: string) => void;
        onDownload: (projectId: string) => void;
        onShare: (projectId: string) => void;
    }>
>;

// Project card list.
export default injectIntl((props: ProjectListProps) => {
    const { projects, isLoading, onCreate, onPublish, onShowPublished, onUpdate, onDelete, onDownload, intl } = props;
    const styles = useAppStyles();
    const [deleteTarget, setDeleteTarget] = useState<Project>();
    const [updateTarget, setUpdateTarget] = useState<Project>();
    const [sortOption, setSortOption] = useState<SortOption>('lastUpdated-desc');

    const sortedProjects = useMemo(() => {
        const compareFunctions: { [key in SortOption]: (a: Project, b: Project) => number } = {
            'title-asc': (a, b) => compareNoCase(a.title, b.title),
            'title-desc': (a, b) => compareNoCase(b.title, a.title),
            'lastUpdated-asc': (a, b) => compareDates(a.lastUpdated, b.lastUpdated),
            'lastUpdated-desc': (a, b) => compareDates(b.lastUpdated, a.lastUpdated),
        };
        return [...projects].sort(compareFunctions[sortOption]);
    }, [projects, sortOption]);

    const sortOptionStrings: { [key in SortOption]: string } = {
        'title-asc': intl.formatMessage({
            description: 'Sort project list alphabetically by title',
            defaultMessage: 'Title (A-Z)',
        }),
        'title-desc': intl.formatMessage({
            description: 'Sort project list in reverse alphabetical order by title',
            defaultMessage: 'Title (Z-A)',
        }),
        'lastUpdated-asc': intl.formatMessage({
            description: 'Sort project list by last updated (oldest first)',
            defaultMessage: 'Last Updated (Oldest first)',
        }),
        'lastUpdated-desc': intl.formatMessage({
            description: 'Sort project list by last updated (newest first)',
            defaultMessage: 'Last Updated (Newest first)',
        }),
    };

    return (
        <TrackContext.Provider value={{ container: 'project-list' }}>
            {!isLoading && !projects.length && <EmptyState onCreate={onCreate} />}
            {(isLoading || !!projects.length) && (
                <div>
                    {isLoading && <LoadingCard showImage />}
                    {!isLoading && (
                        <div className={styles.flexColumnSmallGap}>
                            <div className={styles.flexRowSmallGapCentered}>
                                <Label htmlFor="project-sort-dropdown">
                                    {intl.formatMessage({
                                        description: 'Label for a sort option dropdown',
                                        defaultMessage: 'Sort by:',
                                    })}
                                </Label>
                                <Dropdown
                                    id="project-sort-dropdown"
                                    defaultSelectedOptions={[sortOption]}
                                    defaultValue={sortOptionStrings[sortOption]}
                                    onOptionSelect={(_event, data) => setSortOption(data.optionValue as SortOption)}
                                >
                                    {Object.entries(sortOptionStrings).map(([value, label]) => (
                                        <Option key={value} value={value}>
                                            {label}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.cardsContainer}>
                                {sortedProjects.map((item) => (
                                    <ProjectCard
                                        key={item.projectId}
                                        project={item}
                                        onPublish={() => onPublish(item.projectId)}
                                        onShowPublished={onShowPublished}
                                        onDelete={() => setDeleteTarget(item)}
                                        onUpdate={() => setUpdateTarget(item)}
                                        onDownload={() => onDownload(item.projectId)}
                                        onShare={() => props.onShare(item.projectId)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <DeleteProjectDialog
                open={!!deleteTarget}
                onOpenChange={() => setDeleteTarget(undefined)}
                projectTitle={deleteTarget?.title ?? ''}
                onDelete={() => onDelete(deleteTarget!.projectId)}
                onDownload={() => onDownload(deleteTarget!.projectId)}
            />

            <UpdateProjectDialog
                open={!!updateTarget}
                onOpenChange={() => setUpdateTarget(undefined)}
                initialTitle={updateTarget?.title ?? ''}
                initialDescription={updateTarget?.description}
                onUpdate={(title, description) => onUpdate({ ...updateTarget!, title, description })}
            />
        </TrackContext.Provider>
    );
});
